import { TCampaign } from 'gql'
import { Circle } from 'progressbar.js'
import type CircleType from 'progressbar.js/circle'
import { useEffect, useRef, useState } from 'react'
import { displayPercentage, formatAmount, smartProgressPercentage, t } from 'shared'

export const CampaignProgress = ({
  campaign: { goalCents, receivedCents },
}: {
  campaign: Pick<TCampaign, 'goalCents' | 'receivedCents'>
}) => {
  const progress = smartProgressPercentage(receivedCents, goalCents ?? 0)

  return (
    <div style={{ display: 'flex', marginTop: '0.25em' }}>
      <div className="item left-item">
        <strong className="text-black">{formatAmount({ cents: receivedCents })}</strong>
        <br />
        <small>{t('campaign_progress.collected')}</small>
      </div>

      <div className="item center-item">
        <ProgressCircle progress={progress} />
      </div>

      <div className="item right-item">
        <strong className="text-black">{formatAmount({ cents: goalCents ?? 0 })}</strong>
        <br />
        <small>{t('campaign_progress.goal')}</small>
      </div>
    </div>
  )
}

const ProgressCircle = ({ progress }: { progress: number }) => {
  const [circle, setCircle] = useState<CircleType | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // On mount, we prepare (and destory) the circle
  useEffect(() => {
    const newCircle = new Circle(containerRef.current, {
      ...circleStyle,
    })
    setCircle(newCircle)

    return () => newCircle && newCircle.destroy()
  }, [])

  // On the second render, we update it with data if present.
  useEffect(() => {
    if (!circle) return

    // ProgressBar .animate expect a progress from 0 to 1.
    //  https://progressbarjs.readthedocs.io/en/latest/api/shape/?q=animate&check_keywords=yes&area=default#animateprogress-options-cb
    const progressAsint = progress / 100
    circle.animate(progressAsint)
    circle.setText(`${displayPercentage(progress)}%`)
  }, [circle, progress])

  return <div className="progress-circle" ref={containerRef} />
}

const circleStyle = {
  strokeWidth: 10,
  duration: 1600,
  easing: 'linear',
  color: 'var(--progressColor)',
  trailWidth: 2,
  svgStyle: {
    width: '100px',
    height: '100px',
  },
  text: {
    style: {
      'position': 'absolute',
      'white-space': 'nowrap',
      'left': '50%',
      'top': '50%',
      'font-size': '18px',
      'font-weight': '600',
      'color': '#000',
      'transform': {
        prefix: true,
        value: 'translate(-50%, -50%)',
      },
    },
  },
}
